import type { TFunction } from "i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuthSession } from "../../auth/keycloak.js";
import { facilityApi } from "../../api/client.js";

/** A failed move — carries the HTTP status + the contract's machine `code` so the dialog can map friendly copy. */
export interface MoveFacilityError extends Error {
  status: number;
  code?: string;
  /** The raw response body — preserved so the shared error dialog can render structured detail. */
  body?: unknown;
}

export interface MoveFacilityVars {
  id: number;
  /** Destination parent, or `null` to move to a realm root (bypass tier only — the server enforces). */
  newParentId: number | null;
}

/** Machine code → friendly copy for a failed move. The server is authoritative; a residual 403/400/409/404 lands here
 *  (ADR-005 panel). Shared by the move drawer (picker) and the drag-drop confirm. */
const MOVE_ERROR_KEY = {
  FACILITY_MOVE_CYCLE: "facilityManagement.errors.moveCycle",
  FACILITY_FORBIDDEN: "facilityManagement.errors.moveForbidden",
  FACILITY_ARCHIVED: "facilityManagement.errors.moveArchived",
  FACILITY_NOT_FOUND: "facilityManagement.errors.moveGone",
} as const;
export function moveErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as MoveFacilityError | undefined)?.code;
  const key = code && code in MOVE_ERROR_KEY ? MOVE_ERROR_KEY[code as keyof typeof MOVE_ERROR_KEY] : "facilityManagement.errors.moveFailed";
  return t(key);
}

/**
 * Move a facility to a new parent — the app's first mutation (the reusable `useMutation` pattern). The shared ts-rest
 * transport RESOLVES every response and never throws (api/client.ts), so the `mutationFn` inspects `res.status` and
 * **throws a typed error on any non-200** (mirrors `useFacilities`) — otherwise a 403/409/400/404 would look like a
 * success. `onSuccess` invalidates the facilities query with the v5 **filters-object** form (the positional-array
 * overload was removed in v5); the bare prefix `["facilities", realm, sub]` also matches `useFacilities`' trailing
 * `permission` segment, so the tree refetches at its new shape.
 */
export function useMoveFacility(session: AuthSession | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newParentId }: MoveFacilityVars) => {
      const res = await facilityApi.moveFacility({ params: { id }, body: { newParentId } });
      if (res.status !== 200) {
        const err = new Error(`move failed (${res.status})`) as MoveFacilityError;
        err.status = res.status;
        err.code = (res.body as { code?: string } | undefined)?.code;
        err.body = res.body;
        throw err;
      }
      return res.body.facilities;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["facilities", session?.realm, session?.sub] });
    },
  });
}
